/**
 * Daily fisherman's almanac.
 *
 * Pure, dependency-free summarisation of the data the site already stores
 * (Open-Meteo forecast + marine snapshot + NOAA tide predictions) into the
 * handful of facts an angler actually acts on: today's sky/wind, the tide
 * windows worth fishing, and short practical notes.
 *
 * No upstream call happens here — callers pass already-fetched payloads.
 */

export const ALMANAC_TZ = "America/Anchorage";

export type AlmanacTide = {
  type: "H" | "L";
  timeMs: number;
  heightFt: number;
  /** Slack window around the extreme, in ms. */
  windowStartMs: number;
  windowEndMs: number;
};

export type AlmanacNote = {
  id: string;
  tone: "good" | "caution" | "info";
  text: string;
};

export type Almanac = {
  dateLabel: string;
  sunriseMs: number | null;
  sunsetMs: number | null;
  daylightLabel: string | null;
  highF: number | null;
  lowF: number | null;
  skyCode: number | null;
  precipChancePct: number | null;
  windMaxKn: number | null;
  windDirDeg: number | null;
  seasFt: number | null;
  waterTempF: number | null;
  pressureMb: number | null;
  pressureTrend: "rising" | "falling" | "steady";
  pressureDelta: number;
  tides: AlmanacTide[];
  nextTide: AlmanacTide | null;
  bestWindow: AlmanacTide | null;
  notes: AlmanacNote[];
  /** True when nothing usable could be derived from the inputs. */
  empty: boolean;
};

type WeatherPayload = {
  current?: Record<string, number | string>;
  hourly?: { time: string[]; [k: string]: any };
  daily?: { time: string[]; [k: string]: any };
} | null;

type MarinePayload = {
  current?: { wave_height?: number; sea_surface_temperature?: number };
} | null;

export type AlmanacTideInput = { t: string; v: string; type: "H" | "L" };

const MIN = 60_000;
const HOUR = 60 * MIN;

/**
 * Open-Meteo returns naive local timestamps ("2026-07-29T04:50") when a
 * timezone is requested. `new Date()` would read those as the *runtime's*
 * local zone, which throws sunrise/sunset off by the UTC offset. Parse them
 * explicitly as Alaska wall-clock time.
 */
export function parseZonedIso(value: string, tz = ALMANAC_TZ): number {
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(value.trim());
  if (hasZone) return new Date(value).getTime();
  const naive = `${value.trim().replace(" ", "T")}`.padEnd(19, ":00").slice(0, 19);
  const guess = Date.parse(`${naive}Z`);
  if (!Number.isFinite(guess)) return NaN;
  // Offset of the zone at (approximately) that instant.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(new Date(guess));
  const g = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  const asUtc = Date.parse(`${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}:${g("second")}Z`);
  const offset = asUtc - guess;
  return guess - offset;
}

function num(value: unknown): number | null {
  const n = typeof value === "string" ? Number(value) : (value as number);
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

function dayKey(ms: number, tz = ALMANAC_TZ) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(ms));
}

export function formatAlmanacTime(ms: number, tz = ALMANAC_TZ) {
  return new Date(ms).toLocaleTimeString("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Slack lasts longer on small tide swings and shorter on big ones. Clamped to
 * the 30–120 min band used elsewhere on the signage boards.
 */
export function slackMinutes(rangeFt: number): number {
  if (!Number.isFinite(rangeFt) || rangeFt <= 0) return 60;
  const minutes = 120 - rangeFt * 5;
  return Math.round(Math.min(120, Math.max(30, minutes)));
}

function pressureTrend(hourly: { time: string[]; pressure_msl?: number[] } | undefined, nowMs: number) {
  const series = hourly?.pressure_msl;
  if (!series?.length || !hourly?.time?.length) return { trend: "steady" as const, delta: 0, value: null };
  let idx = hourly.time.findIndex((t) => parseZonedIso(t) >= nowMs);
  if (idx === -1) idx = series.length - 1;
  const start = Math.max(0, idx - 3);
  const delta = (series[idx] ?? 0) - (series[start] ?? 0);
  const trend: "rising" | "falling" | "steady" =
    delta > 0.8 ? "rising" : delta < -0.8 ? "falling" : "steady";
  return { trend, delta, value: num(series[idx]) };
}

function buildTides(raw: AlmanacTideInput[] | null | undefined, nowMs: number): AlmanacTide[] {
  const today = dayKey(nowMs);
  const parsed = (raw ?? [])
    .map((t) => ({ type: t.type, timeMs: parseZonedIso(t.t), heightFt: num(t.v) ?? 0 }))
    .filter((t) => Number.isFinite(t.timeMs) && dayKey(t.timeMs) === today)
    .sort((a, b) => a.timeMs - b.timeMs);

  return parsed.map((t, i) => {
    const neighbour = parsed[i + 1] ?? parsed[i - 1];
    const range = neighbour ? Math.abs(neighbour.heightFt - t.heightFt) : 0;
    const half = (slackMinutes(range) / 2) * MIN;
    return { ...t, windowStartMs: t.timeMs - half, windowEndMs: t.timeMs + half };
  });
}

function buildNotes(a: Omit<Almanac, "notes" | "empty">): AlmanacNote[] {
  const notes: AlmanacNote[] = [];

  if (a.pressureTrend === "rising") {
    notes.push({
      id: "baro",
      tone: "good",
      text: "Pressure is climbing — fish usually feed hard on the front side of a rising barometer.",
    });
  } else if (a.pressureTrend === "falling") {
    notes.push({
      id: "baro",
      tone: "caution",
      text: "Pressure is dropping. Bite often turns on just before the change, then slows — fish early.",
    });
  } else {
    notes.push({ id: "baro", tone: "info", text: "Steady barometer — expect a normal, predictable bite." });
  }

  if (a.windMaxKn != null) {
    if (a.windMaxKn >= 20) {
      notes.push({
        id: "wind",
        tone: "caution",
        text: `Wind to ${Math.round(a.windMaxKn)} kn today. Stay inside the protected shorelines and check the forecast before you run.`,
      });
    } else if (a.windMaxKn <= 10) {
      notes.push({
        id: "wind",
        tone: "good",
        text: `Light wind (to ${Math.round(a.windMaxKn)} kn) — a good day to run out to the outside structure.`,
      });
    } else {
      notes.push({
        id: "wind",
        tone: "info",
        text: `Moderate wind to ${Math.round(a.windMaxKn)} kn — a chop-friendly plan beats a long open-water run.`,
      });
    }
  }

  if (a.seasFt != null && a.seasFt >= 4) {
    notes.push({
      id: "seas",
      tone: "caution",
      text: `Seas near ${a.seasFt.toFixed(1)} ft. Small-boat comfort drops fast above 3 ft — pick sheltered water.`,
    });
  }

  if (a.bestWindow) {
    const label = a.bestWindow.type === "H" ? "high" : "low";
    notes.push({
      id: "tide",
      tone: "good",
      text: `Best tide window: ${formatAlmanacTime(a.bestWindow.windowStartMs)}–${formatAlmanacTime(
        a.bestWindow.windowEndMs,
      )} around the ${label} at ${formatAlmanacTime(a.bestWindow.timeMs)}. Moving water on either side of slack is where the bite is.`,
    });
  }

  if (a.precipChancePct != null && a.precipChancePct >= 60) {
    notes.push({
      id: "rain",
      tone: "info",
      text: `${Math.round(a.precipChancePct)}% chance of rain — bring layers; overcast light often extends the morning bite.`,
    });
  }

  if (a.waterTempF != null) {
    notes.push({
      id: "water",
      tone: "info",
      text: `Water is about ${Math.round(a.waterTempF)}°F. Slow your presentation when it runs cold.`,
    });
  }

  notes.push({
    id: "self-guided",
    tone: "info",
    text: "You run your own day on the water — plan your route and turnaround time before you leave the dock.",
  });

  return notes;
}

export function buildAlmanac(input: {
  weather: WeatherPayload;
  marine: MarinePayload;
  tides: AlmanacTideInput[] | null | undefined;
  now?: number;
}): Almanac {
  const nowMs = input.now ?? Date.now();
  const today = dayKey(nowMs);
  const daily = input.weather?.daily;
  const dIdx = daily?.time?.findIndex((d: string) => d.slice(0, 10) === today) ?? -1;
  const at = <T,>(key: string): T | null =>
    dIdx >= 0 && daily?.[key] ? ((daily[key][dIdx] ?? null) as T) : null;

  const sunriseIso = at<string>("sunrise");
  const sunsetIso = at<string>("sunset");
  const sunriseMs = sunriseIso ? parseZonedIso(sunriseIso) : null;
  const sunsetMs = sunsetIso ? parseZonedIso(sunsetIso) : null;
  const daylightLabel =
    sunriseMs != null && sunsetMs != null && sunsetMs > sunriseMs
      ? `${Math.floor((sunsetMs - sunriseMs) / HOUR)}h ${Math.round(((sunsetMs - sunriseMs) % HOUR) / MIN)}m`
      : null;

  const baro = pressureTrend(input.weather?.hourly, nowMs);
  const tides = buildTides(input.tides, nowMs);
  const nextTide = tides.find((t) => t.timeMs >= nowMs) ?? null;
  const upcoming = tides.filter((t) => t.windowEndMs >= nowMs);
  // Prefer the next upcoming window; fall back to the day's biggest swing.
  const bestWindow =
    upcoming.find((t) => t.type === "H") ?? upcoming[0] ?? tides[tides.length - 1] ?? null;

  const core = {
    dateLabel: new Date(nowMs).toLocaleDateString("en-US", {
      timeZone: ALMANAC_TZ,
      weekday: "long",
      month: "long",
      day: "numeric",
    }),
    sunriseMs,
    sunsetMs,
    daylightLabel,
    highF: num(at<number>("temperature_2m_max")),
    lowF: num(at<number>("temperature_2m_min")),
    skyCode: num(at<number>("weather_code")),
    precipChancePct: num(at<number>("precipitation_probability_max")),
    windMaxKn: num(at<number>("wind_speed_10m_max")),
    windDirDeg: num(at<number>("wind_direction_10m_dominant")),
    seasFt: num(input.marine?.current?.wave_height),
    waterTempF: num(input.marine?.current?.sea_surface_temperature),
    pressureMb: baro.value ?? num(input.weather?.current?.pressure_msl),
    pressureTrend: baro.trend,
    pressureDelta: baro.delta,
    tides,
    nextTide,
    bestWindow,
  };

  const empty =
    core.highF == null &&
    core.lowF == null &&
    core.windMaxKn == null &&
    tides.length === 0 &&
    core.seasFt == null;

  return { ...core, notes: buildNotes(core), empty };
}

const COMPASS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];

export function compassPoint(deg: number | null): string {
  if (deg == null || !Number.isFinite(deg)) return "—";
  return COMPASS[Math.round(deg / 22.5) % 16];
}

export const ALMANAC_SKY: Record<number, string> = {
  0: "Clear",
  1: "Mainly clear",
  2: "Partly cloudy",
  3: "Overcast",
  45: "Fog",
  48: "Rime fog",
  51: "Light drizzle",
  53: "Drizzle",
  55: "Dense drizzle",
  61: "Light rain",
  63: "Rain",
  65: "Heavy rain",
  71: "Light snow",
  73: "Snow",
  75: "Heavy snow",
  80: "Rain showers",
  81: "Rain showers",
  82: "Heavy showers",
  95: "Thunderstorms",
};

export function skyLabel(code: number | null): string {
  if (code == null) return "—";
  return ALMANAC_SKY[code] ?? "Mixed skies";
}
